import React, { createContext, useContext, useState, useEffect } from 'react';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('AUD');
  const [exchangeRates] = useState({
    AUD: 1,
    USD: 0.65,
    EUR: 0.60,
    GBP: 0.52,
    CAD: 0.88
  });

  useEffect(() => {
    // Load currency preference from localStorage on mount
    const savedCurrency = safeStorage.getItem(STORAGE_KEYS.CURRENCY, 'AUD');
    if (savedCurrency && exchangeRates[savedCurrency]) {
      setCurrency(savedCurrency);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeCurrency = (newCurrency) => {
    if (exchangeRates[newCurrency]) {
      setCurrency(newCurrency);
      safeStorage.setItem(STORAGE_KEYS.CURRENCY, newCurrency);
    }
  };

  const convertPrice = (price, fromCurrency = 'AUD') => {
    const rate = exchangeRates[currency] / exchangeRates[fromCurrency];
    return price * rate;
  };

  const formatPrice = (price, fromCurrency = 'AUD') => {
    const symbols = {
      AUD: '$',
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$'
    };
    
    // Handle price ranges (e.g., "14.50 - 16.50" or "14.50-16.50")
    if (typeof price === 'string' && (price.includes(' - ') || price.includes('-'))) {
      const priceParts = price.split(/\s*-\s*/);
      if (priceParts.length === 2) {
        const minPrice = parseFloat(priceParts[0]);
        const maxPrice = parseFloat(priceParts[1]);
        
        const convertedMin = convertPrice(minPrice, fromCurrency);
        const convertedMax = convertPrice(maxPrice, fromCurrency);
        
        return `${symbols[currency]}${convertedMin.toFixed(2)} - ${symbols[currency]}${convertedMax.toFixed(2)}`;
      }
    }
    
    // Handle single price
    const convertedPrice = convertPrice(price, fromCurrency);
    return `${symbols[currency]}${convertedPrice.toFixed(2)}`;
  };

  const value = {
    currency,
    exchangeRates,
    changeCurrency,
    convertPrice,
    formatPrice
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    console.warn('useCurrency used outside CurrencyProvider - returning default values');
    return {
      currency: 'AUD',
      exchangeRates: { AUD: 1 },
      changeCurrency: () => {},
      convertPrice: (price) => price,
      formatPrice: (price) => `$${price.toFixed(2)}`
    };
  }
  return context;
};

export default CurrencyContext;